import React, { useState, useMemo, useEffect } from 'react';
import { SearchBar, Pagination } from './SharedComponents';
import { 
  useGetPriceHistoryVendorsQuery, 
  useUpdateVendorCrawlFrequencyMutation 
} from '../../../../../reducers/priceHistoryApi';
import TableLoader from '../../../../../components/TableSkelton/TableLoader';

// Duplicated Stepper component to avoid accidental regressions in PriorityVariables
const Stepper = ({ value, onChange = () => {}, isLoading = false }) => (
  <div className="d-flex align-items-center gap-3">
    <button 
      type="button"
      className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center" 
      style={{ width: '24px', height: '24px', border: '1px solid #ced4da', backgroundColor: 'white', color: '#6c757d' }}
      onClick={(e) => {
        e.preventDefault();
        onChange(Math.max(0, parseInt(value || 0, 10) - 1));
      }}
      disabled={isLoading}
    >
      <span style={{ fontSize: '18px', lineHeight: '0' }}>&mdash;</span>
    </button>
    <span style={{ fontWeight: '600', minWidth: '15px', textAlign: 'center' }}>{value}</span>
    <button 
      type="button"
      className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center" 
      style={{ width: '24px', height: '24px', border: '1px solid #ced4da', backgroundColor: 'white', color: '#6c757d' }}
      onClick={(e) => {
        e.preventDefault();
        onChange(parseInt(value || 0, 10) + 1);
      }}
      disabled={isLoading}
    >
      <span style={{ fontSize: '18px', lineHeight: '0' }}>+</span>
    </button>
  </div>
);

const FrequencyVariables = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const { data: vendorsData, isLoading: isVendorsLoading } = useGetPriceHistoryVendorsQuery();
  const [updateFrequency, { isLoading: isUpdating }] = useUpdateVendorCrawlFrequencyMutation();

  const handleUpdate = async (vendorId, field, newValue) => {
    if (newValue < 0) return;
    
    // Find vendor to get existing values
    const vendor = vendorsData?.data?.find(v => v._id === vendorId);
    if (!vendor) return;

    const payload = {
        vendorId,
        crawlFrequency: field === 'crawlFrequency' ? newValue : (vendor.crawlFrequency || 0),
        saleCrawlFrequency: field === 'saleCrawlFrequency' ? newValue : (vendor.saleCrawlFrequency || 0)
    };

    try {
      await updateFrequency(payload).unwrap();
    } catch (error) {
      console.error('Failed to update frequency:', error);
    }
  };

  const processedData = useMemo(() => {
    let data = vendorsData?.data || [];
    if (searchTerm) {
      data = data.filter(v => (v.fullName || v._id).toLowerCase().includes(searchTerm.toLowerCase()));
    }
    return data;
  }, [vendorsData, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [processedData, currentPage]);


  return (
    <div className="p-4 bg-white">
      <SearchBar 
        value={searchTerm} 
        onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }} 
        placeholder="Search vendors..."
      />
      
      <div className="mt-4">
        {isVendorsLoading ? (
            <TableLoader rowsNum={5} cellNum={3} />
        ) : (
            <>
            <div className="table-responsive">
                <table className="table">
                <thead>
                    <tr>
                    <th style={{ borderTop: 'none', fontWeight: 'bold' }}>Vendor</th>
                    <th style={{ borderTop: 'none', fontWeight: 'bold' }}>Crawl Frequency (Days)</th>
                    <th style={{ borderTop: 'none', fontWeight: 'bold' }}>Sale Crawl Frequency (Days)</th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedData.map((vendor) => (
                    <tr key={vendor._id}>
                        <td className="align-middle">{vendor.fullName || vendor._id}</td>
                        <td className="align-middle">
                        <Stepper 
                            value={vendor.crawlFrequency || 0} 
                            onChange={(val) => handleUpdate(vendor._id, 'crawlFrequency', val)}
                            isLoading={isUpdating}
                        />
                        </td>
                        <td className="align-middle">
                        <Stepper 
                            value={vendor.saleCrawlFrequency || 0} 
                            onChange={(val) => handleUpdate(vendor._id, 'saleCrawlFrequency', val)}
                            isLoading={isUpdating}
                        />
                        </td>
                    </tr>
                    ))}
                    {paginatedData.length === 0 && <tr><td colSpan="3">No vendors found</td></tr>}
                </tbody>
                </table>
            </div>
            <Pagination 
                currentPage={currentPage}
                totalItems={processedData.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
            />
            </>
        )}
      </div>
    </div>
  );
};

export default FrequencyVariables;
